import { useState } from "react";
import { Link, NavLink, useNavigate } from "react-router-dom";
import { useAuth } from "../contexts/AuthContext";
import { Menu, X } from "lucide-react";
import LogoMark from "./LogoMark";

const navItems = [
  { to: "/episodes", label: "Interviews" },
  { to: "/articles", label: "Editorial" },
  { to: "/funding", label: "Funding" },
  { to: "/credit-readiness", label: "Credit Readiness" },
  { to: "/newsletter", label: "Newsletter" },
  { to: "/franchise", label: "Franchise" },
  { to: "/subscribe", label: "Subscribe" },
];

export default function Navbar() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);

  const handleLogout = async () => {
    await logout();
    navigate("/");
  };

  return (
    <header
      data-testid="navbar"
      className="fixed top-0 left-0 right-0 z-50"
      style={{
        backdropFilter: "blur(20px)",
        WebkitBackdropFilter: "blur(20px)",
        background: "rgba(9, 10, 12, 0.72)",
        borderBottom: "1px solid var(--hairline)",
      }}
    >
      <div className="max-w-[1400px] mx-auto px-6 lg:px-10 py-2 flex items-center justify-between">
        <Link to="/" data-testid="logo-link" className="flex items-center group">
          <LogoMark
            height={96}
            width={96}
            testId="brand-logo"
            className="transition-opacity duration-300 group-hover:opacity-80"
          />
        </Link>

        <nav className="hidden lg:flex items-center gap-10">
          {navItems.map((n) => (
            <NavLink
              key={n.to}
              to={n.to}
              data-testid={`nav-${n.label.toLowerCase()}`}
              className={({ isActive }) =>
                `text-xs uppercase tracking-[0.22em] transition-colors duration-300 ${
                  isActive ? "text-[var(--brass)]" : "text-[var(--ink)] hover:text-[var(--brass)]"
                }`
              }
            >
              {n.label}
            </NavLink>
          ))}
        </nav>

        <div className="hidden lg:flex items-center gap-5">
          {user && user.role === "admin" && (
            <NavLink to="/admin" data-testid="nav-admin" className="text-xs uppercase tracking-[0.22em] text-[var(--brass)]">
              Admin
            </NavLink>
          )}
          {user ? (
            <>
              <span className="text-xs uppercase tracking-[0.18em]" style={{ color: "var(--ink-muted)" }}>
                {user.name?.split(" ")[0] || "Member"}
              </span>
              <button onClick={handleLogout} data-testid="logout-btn" className="btn-ghost !py-2 !px-4">
                Sign Out
              </button>
            </>
          ) : (
            <>
              <Link to="/login" data-testid="nav-login" className="text-xs uppercase tracking-[0.22em] text-[var(--ink)] hover:text-[var(--brass)] transition-colors">
                Sign In
              </Link>
              <Link to="/register" data-testid="nav-register" className="btn-brass !py-2 !px-4">
                Join
              </Link>
            </>
          )}
        </div>

        <button
          data-testid="mobile-menu-toggle"
          className="lg:hidden text-[var(--ink)]"
          onClick={() => setOpen(!open)}
          aria-label="Toggle menu"
        >
          {open ? <X size={22} /> : <Menu size={22} />}
        </button>
      </div>

      {open && (
        <div className="lg:hidden border-t" style={{ borderColor: "var(--hairline)", background: "rgba(9, 10, 12, 0.96)" }}>
          <div className="px-6 py-6 flex flex-col gap-5">
            {navItems.map((n) => (
              <NavLink
                key={n.to}
                to={n.to}
                onClick={() => setOpen(false)}
                data-testid={`mobile-nav-${n.label.toLowerCase()}`}
                className="text-sm uppercase tracking-[0.22em] text-[var(--ink)]"
              >
                {n.label}
              </NavLink>
            ))}
            {user && user.role === "admin" && (
              <NavLink to="/admin" onClick={() => setOpen(false)} className="text-sm uppercase tracking-[0.22em] text-[var(--brass)]">
                Admin
              </NavLink>
            )}
            <hr className="hairline" />
            {user ? (
              <button onClick={() => { setOpen(false); handleLogout(); }} className="btn-ghost self-start">Sign Out</button>
            ) : (
              <div className="flex gap-3">
                <Link to="/login" onClick={() => setOpen(false)} className="btn-ghost">Sign In</Link>
                <Link to="/register" onClick={() => setOpen(false)} className="btn-brass">Join</Link>
              </div>
            )}
          </div>
        </div>
      )}
    </header>
  );
}
